/**
 * WHICH WORKSPACE THE SIGN-IN TOKEN SAYS WE ARE IN.
 *
 * READ, NEVER TRUSTED. The database decides this: it reads the same claim and
 * honours it only where a real membership exists, so a token saying anything
 * else changes nothing about what comes back. This is for putting a name on a
 * screen, and a wrong name would be a cosmetic fault rather than a breach.
 *
 * Decoded by hand because the alternative is a round trip for a value already
 * sitting in the browser. Anything unexpected gives nothing, and every caller
 * falls back to the oldest membership — which is what the database would have
 * chosen anyway.
 *
 * IN ITS OWN FILE BECAUSE TWO SCREENS WANT IT: the switcher in the sidebar and
 * the Organisation panel in the account. The second copy is where the fix stops
 * being applied to the first — that cost an evening on 10 August, when five
 * scripts each held their own copy of one HTTP call.
 */
/** Whatever the sign-in token is carrying, or an empty object. */
function claims(): Record<string, unknown> {
  try {
    const key = Object.keys(localStorage).find((k) => k.startsWith('sb-') && k.endsWith('-auth-token'));
    if (!key) return {};
    const held = JSON.parse(localStorage.getItem(key) ?? '{}');
    const body = String(held.access_token ?? '').split('.')[1];
    if (!body) return {};
    const meta = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/')))?.app_metadata;
    return meta && typeof meta === 'object' ? meta : {};
  } catch {
    return {};
  }
}

export function currentWorkspace(): string {
  return String(claims().tenant ?? '');
}

/**
 * WHETHER THIS IS THE STAFF ACCOUNT.
 *
 * For a label, and for nothing else. The same claim decides real things in the
 * database — see is_support() in 0012 — but there it arrives inside a token
 * Supabase has verified, and here it is read out of local storage, where
 * anybody can write whatever they like. Setting it by hand changes a word on a
 * screen and grants nothing: the grant is a row in the customer's own database
 * and this cannot conjure one.
 */
export function amStaff(): boolean {
  return claims().staff === true;
}

/**
 * WHICH WORKSPACE A TENANT-LEVEL READ OR WRITE SHOULD TARGET — pure, so it can
 * be tested without a browser.
 *
 * The workspace in the ADDRESS wins, because that is what the database's
 * my_tenant() resolves to: the address sets the `x-flightdeck-company` header
 * the policies read, so a member of more than one workspace who has switched to
 * a non-home one is scoped — for every policy — to the VIEWED workspace. The
 * `tenant` claim in the token still names their HOME, so keying the read by the
 * claim (as this did before) asked for the home row through a client scoped to
 * the viewed one: the policy matched nothing and the caller fell silently to
 * defaults — a non-home workspace lost its currency, rates, working calendar and
 * payment-approval chain, and the defaulted calendar made the forecast
 * optimistically wrong with no signal.
 *
 * When the address names no company (a single-company deployment, or before the
 * address has settled) the claim wins, and failing that the OLDEST membership —
 * which is the order my_tenant() itself falls back through (0011/0085), so the
 * id chosen and the row the policy permits still match.
 */
export function pickWorkspaceId(
  viewedSlug: string,
  claimedTenant: string,
  mine: readonly { id: string; slug: string }[],
): string {
  if (viewedSlug) {
    const viewed = mine.find((w) => w.slug === viewedSlug);
    if (viewed) return viewed.id;
  }
  if (claimedTenant) return claimedTenant;
  return mine[0]?.id ?? '';
}
